import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericButton from "@ui-components/Button/GenericButton";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericModal from "@ui-components/Modal/GenericModal";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import { columns as portColumns } from "./PortData";
import { ReactComponent as IconEye } from "../../../../shared/assets/Icons/Icon_Eye.svg";
import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusV3.svg";
import { ReactComponent as IconEdit } from "../../../../shared/assets/Icons/Icon_Edit.svg";
import { ReactComponent as IconDelete } from "../../../../shared/assets/Icons/Icon_Delete.svg";

// Example request functions (replace with your real API calls)
import {
	getHardwareListByDataCenter,
	getPortsById,
	addPort,
	updatePort,
	deletePort,
	getListPortsByHardwareId,
	getAllPorts,
} from "./request/request";

import { getDataCentersIdsNames } from "../Hardware/request/request";

// ---------- Interfaces (Adjust to match your actual API shapes) -----------

// Datacenter
interface DataCenterItem {
	datacenterId: string; // or number
	name: string;
}

interface HardwareDropdownItem {
	id: number;
	hostname: string;
}

// Hardware
interface HardwareItem {
	id: number;
	datacenterId: string;
	serialNumber: string;
	model: string;
	vendor: string;
	hostname: string;
	status: string;
}

// Port
interface Port {
	id: number;
	hardwareResourceId: number;
	portName: string;
	portId: string;
	portNumber: string;
	portType: string;
	portStatus: string;
	l2Description: string;
	l3Description: string;
	clientAssignation?: string;
}

// This shape is returned by getPortsByHardwareId
interface PortsByHardwareResponse {
	hardware: HardwareItem;
	ports: Port[];
}

// ---------- Main Component -----------------------------------------------

export default function PortInventoryPage() {
	// Auth
	const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

	// 1) Datacenter dropdown states
	const [dataCenterOptions, setDataCenterOptions] = useState<string[]>([]);
	const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
	const [, setSelectedDataCenterId] = useState<string | null>("");
	const [selectedDataCenterName, setSelectedDataCenterName] =
		useState<string>("");

	// 2) Hardware dropdown states
	const [hardwareList, setHardwareList] = useState<HardwareDropdownItem[]>([]);
	const [hardwareOptions, setHardwareOptions] = useState<string[]>([]);
	const [selectedHardwareId, setSelectedHardwareId] = useState<number | null>(
		null
	);
	// For the modal's DC + hardware selection
	const [, setModalDcId] = useState<string | null>(null);
	const [modalDcName, setModalDcName] = useState<string>("");
	const [modalHwId, setModalHwId] = useState<number | null>(null);
	const [, setModalHwName] = useState<string>("");

	// 3) Hardware info (read‐only) + Ports list
	const [hardwareData, setHardwareData] = useState<HardwareItem | null>(null);
	const [ports, setPorts] = useState<Port[]>([]);

	// 4) Spinner / error states
	const [isFetching, setIsFetching] = useState(false);

	// 5) Add/Edit Port modal
	const [isPortModalOpen, setIsPortModalOpen] = useState(false);
	const [editMode, setEditMode] = useState(false);

	// Port fields (for Add/Edit)
	const [portId, setPortId] = useState<string>("");
	const [id, setId] = useState<number | null>(null);
	const [portName, setPortName] = useState("");
	const [portNumber, setPortNumber] = useState("");
	const [portType, setPortType] = useState("");
	const [portStatus, setPortStatus] = useState("");
	const [l2Description, setL2Description] = useState("");
	const [l3Description, setL3Description] = useState("");
	const [, setClientAssignation] = useState("");
	const [hardwareResourceId, setHardwareResourceId] = useState<number | null>(
		null
	);

	// 6) Delete confirmation
	const [isAlertOpen, setIsAlertOpen] = useState(false);
	const [portToDelete, setPortToDelete] = useState<Port | null>(null);

	// 7) View modal state
	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [selectedPort, setSelectedPort] = useState<Port | null>(null);
	// --------------------------- useEffects ----------------------------------

	// A) Fetch datacenters on mount (once user is authenticated)
	useEffect(() => {
		if (isLoading) return;
		if (!isAuthenticated) return;

		(async () => {
			try {
				setIsFetching(true);
				const token = await getAccessTokenSilently();
				// Suppose getDataCentersIdsNames returns an array of {id, name}
				const resp = await getDataCentersIdsNames(token);

				// The endpoint might return an array of objects with many fields
				// Shape/filter each object to { datacenterId, name }
				const shapedData: DataCenterItem[] = resp.data.map(
					(dc: DataCenterItem) => ({
						datacenterId: dc.datacenterId,
						name: dc.name,
					})
				);
				setDataCenters(shapedData);
				// Convert to string array of names
				const dcNames = resp.data.map((dc) => dc.name);
				setDataCenterOptions(["All", ...dcNames]);
				// 2) fetch all ports by default
				const allPortsResp = await getAllPorts(token);
				setPorts(allPortsResp.data.ports);
			} catch (error) {
				console.error("Error fetching datacenters:", error);
			} finally {
				setIsFetching(false);
			}
		})();
	}, [isLoading, isAuthenticated, getAccessTokenSilently]);

	//HELPER FUNCTIONS
	// A) Convert the stored ID (selectedHardwareId) into a hardware name
	function getHardwareNameById(hardwareId: number | null): string {
		if (!hardwareId) return "";
		const match = hardwareList.find((hw) => hw.id === hardwareId);
		return match ? match.hostname : "";
	}
	// --------------------------- Handlers ------------------------------------

	// 1) When user picks a DC name
	async function handleDataCenterSelect(dcName: string, isModal = false) {
		if (!isModal) {
			// ========== GLOBAL FILTER LOGIC ==========
			setSelectedDataCenterName(dcName);
			setSelectedHardwareId(null);
			setHardwareData(null);
			setPorts([]);
		} else {
			// ========== MODAL LOGIC ==========
			setModalDcName(dcName);
			setModalHwId(null);
		}

		// Suppose we have to convert name->id. For the example, pretend the name == id
		// 1) Find the matching object by name

		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			if (dcName === "All" && !isModal) {
				// fetch all
				console.log("inside if");
				const allPortsResp = await getAllPorts(token);
				setPorts(allPortsResp.data.ports);
				setSelectedDataCenterName("All");
				setSelectedDataCenterId(null);
				setHardwareOptions([]);
				return;
			}
			const matchedDc = dataCenters.find((dc) => dc.name === dcName);
			if (!matchedDc) {
				console.error("No data center found for name:", dcName);
				return;
			}
			const hwResp = await getHardwareListByDataCenter(
				token,
				matchedDc.datacenterId
			);
			// Save the fields needed for our dropdowns
			const shapedHardware: HardwareDropdownItem[] = hwResp.data.map(
				(hw: HardwareDropdownItem) => ({
					id: hw.id,
					hostname: hw.hostname,
				})
			);
			const hwNames = hwResp.data.map(
				(hw: { id: number; hostname: string }) => hw.hostname
			);
			if (!isModal) {
				setHardwareList(shapedHardware);
				setHardwareOptions(hwNames);
				setSelectedDataCenterId(matchedDc.datacenterId);
			} else {
				setHardwareList(shapedHardware);
				setHardwareOptions(hwNames);
				setModalDcId(matchedDc.datacenterId);
			}
		} catch (err) {
			console.error("Error fetching hardware list:", err);
		} finally {
			setIsFetching(false);
		}
	}

	// 2) When user picks hardware
	async function handleHardwareSelect(hwName: string, isModal = false) {
		if (!isModal) {
			setHardwareData(null);
			setPorts([]);
		} else {
			setModalHwName(hwName);
			setModalHwId(null);
		}

		const matchedHw = hardwareList.find((hw) => hw.hostname === hwName);
		if (!matchedHw) {
			console.error("No hardware found for name:", hwName);
			return;
		}
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			const resp = await getListPortsByHardwareId(token, matchedHw.id);
			const data: PortsByHardwareResponse = resp.data;

			if (!isModal) {
				setHardwareData(data.hardware);
				setPorts(data.ports);
				setSelectedHardwareId(data.hardware.id);
			} else {
				setModalHwId(data.hardware.id);
				setHardwareResourceId(data.hardware.id);
			}
		} catch (err) {
			console.error("Error fetching ports by hardware:", err);
		} finally {
			setIsFetching(false);
		}
	}

	// 3) Add Port
	function handleAddPort() {
		setEditMode(false);
		// Clear out port fields
		setPortId("");
		setPortName("");
		setPortNumber("");
		setPortType("");
		setPortStatus("");
		setL2Description("");
		setL3Description("");
		setClientAssignation("");
		setModalDcId(null);
		setModalDcName("");
		setModalHwId(null);
		setModalHwName("");
		setIsPortModalOpen(true);
	}

	async function handleSavePort() {
		// For add or update
		if (!hardwareResourceId) {
			alert("Please select a hardware first.");
			return;
		}

		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();

			// Build payload from state
			const payload = {
				hardwareResourceId,
				portId,
				portName,
				portNumber,
				portType,
				portStatus,
				l2Description,
				l3Description,
			};
			if (editMode && id != null) {
				// Update an existing port
				await updatePort(token, id, payload);
				setPorts((prevPorts) =>
					prevPorts.map((port) =>
						port.id === id ? { ...port, ...payload } : port
					)
				);
			} else {
				// Add a new port
				await addPort(token, payload);
			}

			// Re-fetch ports
			if (hardwareData) {
				await handleHardwareSelect(String(hardwareData.id));
			}
		} catch (error) {
			console.error("Error saving port:", error);
		} finally {
			setIsFetching(false);
			setIsPortModalOpen(false);
		}
	}

	// 4) Edit Port
	function handleEditPort(row: Port) {
		setEditMode(true);
		setPortId(row.portId);
		setPortName(row.portName);
		setPortNumber(row.portNumber);
		setPortType(row.portType);
		setPortStatus(row.portStatus);
		setL2Description(row.l2Description);
		setL3Description(row.l3Description);
		setHardwareResourceId(row.hardwareResourceId);
		setId(row.id);
		setIsPortModalOpen(true);
	}

	// 5) Delete Port
	function handleDeletePort(row: Port) {
		setPortToDelete(row);
		setIsAlertOpen(true);
	}

	async function confirmDeletePort() {
		if (!portToDelete) return;
		try {
			setIsFetching(true);
			const token = await getAccessTokenSilently();
			await deletePort(token, portToDelete.id);
			// Remove from local list or re-fetch
			setPorts((prev) => prev.filter((p) => p.id !== portToDelete.id));
		} catch (error) {
			console.error("Error deleting port:", error);
		} finally {
			setIsFetching(false);
			setPortToDelete(null);
			setIsAlertOpen(false);
		}
	}
	//6 View Port
	const handleViewPort = async (port: Port) => {
		try {
			const token = await getAccessTokenSilently();
			const response = await getPortsById(token, port.id);
			setSelectedPort(response.data);
			setIsViewModalOpen(true);
		} catch (error) {
			console.error("Error viewing port:", error);
		}
	};

	// ------------------------------------------------------------------------------------
	// Override "Actions" column
	// ------------------------------------------------------------------------------------
	const columns = portColumns.map((col) => {
		if (col.header === "Actions" && col.renderCell) {
			return {
				...col,
				renderCell: (row: Port) => (
					<div className="flex items-center gap-2">
						{/* View */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleViewPort(row)}
						>
							<IconEye className="w-6 h-6" />
						</button>

						{/* Edit */}
						<button
							className="
                flex items-center justify-center
                border border-semantic-border-secondary
                rounded
                text-semantic-text-secondary
                hover:bg-semantic-state-active
                w-10 h-10
              "
							onClick={() => handleEditPort(row)}
						>
							<IconEdit className="w-10 h-10" />
						</button>

						{/* Delete */}
						<button
							className="
                p-2
                border border-semantic-border-secondary
                rounded
                bg-red-50
                text-red-600
                hover:bg-red-100
              "
							onClick={() => handleDeletePort(row)}
						>
							<IconDelete className="w-6 h-6" />
						</button>
					</div>
				),
			};
		}
		return col;
	});

	// ------------------------------------------------------------------------

	return (
		<div className="relative p-4">
			{/* Top Section: Datacenter + Hardware Select */}
			<div className="mb-4 p-4 bg-[var(--semantic-bg-primary)] border border-semantic-border-secondary rounded-sm">
				<div className="flex items-end justify-between">
					{/* Left: Datacenter + Hardware dropdowns */}
					<div className="flex items-end space-x-4">
						{/* 1) Datacenter */}
						<div className="flex flex-col">
							<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
								Select a Data Center
							</label>
							<GenericDropdown
								items={dataCenterOptions}
								defaultLabel="Choose DC..."
								value={selectedDataCenterName}
								onSelect={handleDataCenterSelect}
								minWidthClass="min-w-[200px]"
								maxWidthClass="max-w-[240px]"
							/>
						</div>

						{/* 2) Hardware (only enabled if DC is selected) */}
						<div className="flex flex-col">
							<label className="mb-1 text-sm font-medium text-semantic-text-secondary">
								Select Hardware
							</label>
							<GenericDropdown
								items={hardwareOptions}
								defaultLabel="Choose Hardware..."
								value={getHardwareNameById(selectedHardwareId)}
								onSelect={handleHardwareSelect}
								disabled={!selectedDataCenterName}
								minWidthClass="min-w-[200px]"
								maxWidthClass="max-w-[240px]"
							/>
						</div>
					</div>

					{/* Right: Add new port button */}
					<GenericButton
						label={
							<div className="flex items-center gap-2">
								<IconPlus className="w-4 h-4" />
								<span>Add Port</span>
							</div>
						}
						variant="brand"
						size="md"
						onClick={handleAddPort}
					/>
				</div>
			</div>

			{/* Hardware Details (read‐only)  HIDE FOR THE MOMENT*/}
			{/* {hardwareData && (
				<div className="p-4 mb-4 border border-semantic-border-secondary rounded-sm">
					<h2 className="text-lg font-semibold mb-4">Hardware Details</h2>
					<div className="grid grid-cols-3 gap-4">
						<GenericInput
							label="Data Center ID"
							value={hardwareData.datacenterId}
							readOnly
						/>
						<GenericInput
							label="Hardware ID"
							value={getHardwareNameById(selectedHardwareId)}
							readOnly
						/>
						<GenericInput
							label="Serial Number"
							value={hardwareData.serialNumber}
							readOnly
						/>
						<GenericInput label="Model" value={hardwareData.model} readOnly />
						<GenericInput label="Vendor" value={hardwareData.vendor} readOnly />
						<GenericInput
							label="Hostname"
							value={hardwareData.hostname}
							readOnly
						/>
					</div>
				</div>
			)} */}

			{/* Ports Table (List) */}
			<div className="p-4 border border-semantic-border-secondary rounded-sm">
				<h2 className="text-lg font-semibold mb-4">Ports</h2>
				<GenericTable
					columns={columns}
					data={ports}
					initialRowsPerPage={5}
					rowsPerPageOptions={[5, 10]}
					tableClassName="bg-[var(--semantic-bg-primary)]"
					width="100%"
				/>
			</div>

			{/* ====================== Modal Add / Edit Port ====================== */}
			<GenericModal
				isOpen={isPortModalOpen}
				onClose={() => setIsPortModalOpen(false)}
				title={editMode ? "Edit Port" : "Add Port"}
				size="md"
			>
				<div className="grid grid-cols-2 gap-4 mb-4">
					<GenericInput
						label="Port Name"
						value={portName}
						onChange={setPortName}
					/>
					<GenericInput label="Port ID" value={portId} onChange={setPortId} />
					<GenericInput
						label="Port Number"
						value={portNumber}
						onChange={setPortNumber}
					/>
					<GenericInput
						label="Port Type"
						value={portType}
						onChange={setPortType}
					/>
					<GenericInput
						label="Status"
						value={portStatus}
						onChange={setPortStatus}
					/>
					<GenericInput
						label="L2 Description"
						value={l2Description}
						onChange={setL2Description}
					/>
					<GenericInput
						label="L3 Description"
						value={l3Description}
						onChange={setL3Description}
					/>
					{!editMode && (
						<div className="flex flex-col space-y-2">
							<label>Data Center</label>
							<GenericDropdown
								items={dataCenterOptions}
								value={modalDcName}
								onSelect={(dcName) => handleDataCenterSelect(dcName, true)}
								defaultLabel="Select DC"
							/>
						</div>
					)}
					<div className="flex flex-col space-y-2">
						<label>Hardware</label>
						<GenericDropdown
							items={hardwareOptions}
							value={getHardwareNameById(modalHwId)}
							onSelect={(hwName) => handleHardwareSelect(hwName, true)}
							disabled={!modalDcName} // only active if DC is picked
						/>
					</div>
					{/* <GenericInput
						label="Client Assignation"
						value={clientAssignation}
						onChange={setClientAssignation}
					/> */}
				</div>
				<div className="flex justify-end gap-2">
					<GenericButton
						label="Cancel"
						variant="secondary"
						onClick={() => setIsPortModalOpen(false)}
					/>
					<GenericButton
						label="Save"
						variant="brand"
						onClick={handleSavePort}
					/>
				</div>
			</GenericModal>

			<GenericModal
				isOpen={isViewModalOpen}
				onClose={() => setIsViewModalOpen(false)}
				title="View Port Details"
				size="md"
			>
				{selectedPort && (
					<div className="grid grid-cols-2 gap-4 mb-4">
						{/* For each field, display a read-only input */}
						<GenericInput
							label="Port ID"
							value={String(selectedPort.portId)}
							readOnly
						/>
						<GenericInput
							label="Port Name"
							value={selectedPort.portName}
							readOnly
						/>
						<GenericInput
							label="Port Number"
							value={selectedPort.portNumber}
							readOnly
						/>
						<GenericInput
							label="Port Type"
							value={selectedPort.portType}
							readOnly
						/>
						<GenericInput
							label="Port Status"
							value={selectedPort.portStatus}
							readOnly
						/>
					</div>
				)}

				<div className="flex justify-end">
					<GenericButton
						label="Close"
						variant="secondary"
						onClick={() => setIsViewModalOpen(false)}
					/>
				</div>
			</GenericModal>
			{/* Delete Confirmation Alert */}
			<GenericAlert
				isOpen={isAlertOpen}
				onClose={() => setIsAlertOpen(false)}
				onConfirm={confirmDeletePort}
				title="Are you sure?"
				description="This action cannot be undone. This will permanently delete the port."
				cancelLabel="Cancel"
				confirmLabel="Delete"
				iconWidthClass="w-20 h-12"
			/>

			{/* Overlay / Spinner */}
			{isFetching && (
				<div
					className="
            fixed
            inset-0
            flex flex-col items-center justify-center
            bg-black/50
            z-[9999]
          "
				>
					<h2 className="text-xl font-semibold mb-4 text-white">
						Processing request...
					</h2>
					<div
						className="
              animate-spin
              rounded-full
              h-12 w-12
              border-4
              border-gray-300
              border-t-blue-500
            "
					/>
				</div>
			)}
		</div>
	);
}
